import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import useToasts from '@/hooks/useToasts';
import { api } from '@/utils/api';
import { CoverImagesProjectStatus } from '@/utils/enums';

import Progress from './Progress';

const Box = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 48px;
`;

interface Props {
  images: string[];
  selectedImages: number[];
  isAiGenerated?: boolean;
  onReset: () => void;
}

const PostToArticleButton: FC<Props> = ({
  images,
  selectedImages,
  isAiGenerated,
  onReset,
}) => {
  const [progressCounter, setProgressCounter] = useState(0);

  const { id } = useParams();

  const [isPosting, setIsPosting] = useState(false);

  const queryClient = useQueryClient();

  const toasts = useToasts();

  const onPost = async () => {
    const toastId = toasts.loading();

    try {
      setIsPosting(true);
      const filteredImages = images?.filter((_, index) =>
        selectedImages.includes(index),
      );

      for (let image of filteredImages) {
        setProgressCounter((prev) => prev + 1);

        await api.post(`/v1/cover-images/post-to-article/${id}`, {
          image: image,
          ...(isAiGenerated && { is_ai_generated: true }),
        });
      }

      await api.post(`/v1/cover-images/send-slack-notif/${id}`, {
        status: CoverImagesProjectStatus.PENDING,
      });

      await queryClient.invalidateQueries(['cover_images_project', { id }], {
        exact: true,
      });

      toasts.success(toastId);

      onReset();
    } catch (_) {
      toasts.error(toastId);
    }
    setIsPosting(false);
  };

  return (
    <Box>
      <Button loading={isPosting} disabled={isPosting} onClick={onPost}>
        Move Selected to Pending
      </Button>
      <AnimatePresence>
        {!!isPosting && (
          <Progress
            text="Uploading images..."
            progressPercent={
              (progressCounter /
                images.filter((_, index) => selectedImages.includes(index))
                  .length) *
              100
            }
          />
        )}
      </AnimatePresence>
    </Box>
  );
};

export default PostToArticleButton;
